<template>
  <BaseNavigationBar title="Редактирование шаблона" />

  <BaseIsland>
    <form class="five-template" @submit.prevent="handleSave">
      <div class="five-template__header">
        <BaseForm
          ref="formRef1"
          v-model="formModel"
          :schema="fiveTemplateHeaderSchema"
          :with-form-wrapper="false"
          :with-error-message="false"
        />
        <TemplateLandingButtonSlot
          :button="buttons.header"
          @add="handleStartAddButton('header')"
          @edit="handleOpenModuleSelect('header')"
          @clear="handleClearButton('header')"
        />
      </div>

      <div class="five-template__triple-images">
        <TemplateMediaSlot
          :model-value="images[0]"
          @update:model-value="(urls) => handleUpdateImage(0, urls)"
        />
        <TemplateMediaSlot
          :model-value="images[1]"
          @update:model-value="(urls) => handleUpdateImage(1, urls)"
        />
        <TemplateMediaSlot
          :model-value="images[2]"
          @update:model-value="(urls) => handleUpdateImage(2, urls)"
        />
      </div>

      <div class="five-template__two-section">
        <div class="five-template__additional-section">
          <BaseButton
            variant="dark"
            rounded
            class="five-template__section-marker"
          >
            1
          </BaseButton>
          <BaseForm
            ref="formRef2"
            v-model="formModel"
            :schema="fiveTemplateLeftHeaderSchema"
            :with-form-wrapper="false"
            :with-error-message="false"
          />
          <TemplateLandingButtonSlot
            full-width
            :button="buttons.leftSection"
            @add="handleStartAddButton('leftSection')"
            @edit="handleOpenModuleSelect('leftSection')"
            @clear="handleClearButton('leftSection')"
          />
        </div>
        <div class="five-template__additional-section">
          <BaseButton
            variant="dark"
            rounded
            class="five-template__section-marker"
          >
            2
          </BaseButton>
          <BaseForm
            ref="formRef3"
            v-model="formModel"
            :schema="fiveTemplateRightHeaderSchema"
            :with-form-wrapper="false"
            :with-error-message="false"
          />
          <TemplateLandingButtonSlot
            full-width
            :button="buttons.rightSection"
            @add="handleStartAddButton('rightSection')"
            @edit="handleOpenModuleSelect('rightSection')"
            @clear="handleClearButton('rightSection')"
          />
        </div>
      </div>

      <div class="five-template__additional-section">
        <BaseForm
          ref="formRef4"
          v-model="formModel"
          :schema="fiveTemplateAdditionalSectionSchema"
          :with-form-wrapper="false"
          :with-error-message="false"
        />
        <TemplateLandingButtonSlot
          :button="buttons.additional"
          @add="handleStartAddButton('additional')"
          @edit="handleOpenModuleSelect('additional')"
          @clear="handleClearButton('additional')"
        />
      </div>

      <div class="five-template__two-section">
        <TemplateMediaSlot
          :model-value="images[3]"
          @update:model-value="(urls) => handleUpdateImage(3, urls)"
        />
        <TemplateMediaSlot
          :model-value="images[4]"
          @update:model-value="(urls) => handleUpdateImage(4, urls)"
        />
      </div>

      <div class="five-template__additional-section">
        <BaseForm
          ref="formRef5"
          v-model="formModel"
          :schema="fiveTemplateAdditionalSecondarySectionSchema"
          :with-form-wrapper="false"
          :with-error-message="false"
        />
        <TemplateLandingButtonSlot
          :button="buttons.secondaryAdditional"
          @add="handleStartAddButton('secondaryAdditional')"
          @edit="handleOpenModuleSelect('secondaryAdditional')"
          @clear="handleClearButton('secondaryAdditional')"
        />
      </div>

      <BaseButton
        variant="primary"
        type="submit"
        max-width="335px"
        with-full
        with-decor
        :loading="loading.value"
        :disabled="loading.value"
      >
        Сохранить
      </BaseButton>
    </form>
  </BaseIsland>

  <LandingButtonTypePopup
    v-model="isButtonTypePopupOpen"
    @select-module="handleChooseButtonType('module')"
    @select-object="handleChooseButtonType('moduleObject')"
  />
</template>

<script setup lang="ts">
import type { IFiveFormData, IFiveTemplateData } from '~/entities/projects/model/templates-interface'
import type { IFormExpose } from '~/shared/ui'
import { ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useTemplateEditProjectOrRequestMeta } from '~/entities/projects'
import { templateId } from '~/entities/projects/model/const'
import { navigateAfterTemplateSave } from '~/entities/projects/model/navigateAfterTemplateSave'
import { validateTemplateImages } from '~/entities/projects/model/templateImageValidation'
import {
  fiveTemplateAdditionalSecondarySectionSchema,
  fiveTemplateAdditionalSectionSchema,
  fiveTemplateHeaderSchema,
  fiveTemplateLeftHeaderSchema,
  fiveTemplateRightHeaderSchema,
} from '~/entities/projects/model/templates-schema'
import { uploadProjectImage } from '~/entities/projects/model/uploadProjectImage'
import { useTemplateEditButtons } from '~/entities/projects/model/useTemplateEditButtons'
import { useTemplateManager } from '~/entities/projects/model/useTemplateManager'
import LandingButtonTypePopup from '~/entities/projects/ui/templates/LandingButtonTypePopup.vue'
import TemplateLandingButtonSlot from '~/entities/projects/ui/templates/TemplateLandingButtonSlot.vue'
import TemplateMediaSlot from '~/entities/projects/ui/templates/TemplateMediaSlot.vue'
import { permissions, useLoading } from '~/shared/lib'
import {
  BaseButton,
  BaseForm,
  BaseIsland,
  BaseNavigationBar,
} from '~/shared/ui'

const router = useRouter()
const route = useRoute()

const { uploadFromBlobUrl } = uploadProjectImage()
const { isProjectRequestContext, resolvedProjectTitle, projectId, routeParamForLinks } = useTemplateEditProjectOrRequestMeta()
const { hasExistingTemplate, existingTemplate, saveTemplate } = useTemplateManager(() =>
  isProjectRequestContext.value
    ? { projectRequestId: route.params.id as string }
    : { projectId: projectId.value },
)

const images = ref<string[]>([])
const loading = useLoading()

const formModel = ref<IFiveFormData>({
  title: '',
  description: '',
  leftSectionTitle: '',
  leftSectionDescription: '',
  rightSectionTitle: '',
  rightSectionDescription: '',
  bottomSectionTitle: '',
  bottomSectionDescription: '',
  bottomSecondarySectionTitle: '',
  bottomSecondaryDescription: '',
})

const {
  buttons,
  isButtonTypePopupOpen,
  loadButtonsFromContent,
  handleStartAddButton,
  handleOpenModuleSelect,
  handleChooseButtonType,
  handleClearButton,
  getButtonsForSave,
  clearDraft,
} = useTemplateEditButtons({
  templateNumericId: templateId.five_template,
  editRoutePermission: permissions.PROJECTS.WORKSPACE.ABOUT.TEMPLATES.FIVE_TEMPLATE.EDIT.PERMISSION,
  getDraftPayload: () => ({
    formModel: { ...formModel.value },
    images: [...images.value],
    buttons: { ...buttons.value },
  }),
  applyDraftPayload: (draft) => {
    formModel.value = draft.formModel as IFiveFormData
    images.value = [...draft.images]
    buttons.value = { ...draft.buttons }
  },
})

const formRef1 = ref<ComponentPublicInstance<IFormExpose>>()
const formRef2 = ref<ComponentPublicInstance<IFormExpose>>()
const formRef3 = ref<ComponentPublicInstance<IFormExpose>>()
const formRef4 = ref<ComponentPublicInstance<IFormExpose>>()
const formRef5 = ref<ComponentPublicInstance<IFormExpose>>()

watch(existingTemplate, (template) => {
  if (template?.content && hasExistingTemplate.value) {
    const content = template.content as unknown as IFiveTemplateData

    if (content.id === templateId.five_template) {
      formModel.value = {
        title: content.title || resolvedProjectTitle.value,
        description: content.description || '',
        leftSectionTitle: content.leftSectionTitle || '',
        leftSectionDescription: content.leftSectionDescription || '',
        rightSectionTitle: content.rightSectionTitle || '',
        rightSectionDescription: content.rightSectionDescription || '',
        bottomSectionTitle: content.bottomSectionTitle || '',
        bottomSectionDescription: content.bottomSectionDescription || '',
        bottomSecondarySectionTitle: content.bottomSecondarySectionTitle || '',
        bottomSecondaryDescription: content.bottomSecondaryDescription || '',
      }

      if (content.images) {
        images.value = [
          content.images.triple1 || '',
          content.images.triple2 || '',
          content.images.triple3 || '',
          content.images.bottom1 || '',
          content.images.bottom2 || '',
        ]
      }

      loadButtonsFromContent(content.buttons)
    }
  }
}, { immediate: true })

watch(
  resolvedProjectTitle,
  (name) => {
    if (name && !formModel.value.title) {
      formModel.value.title = name
    }
  },
  { immediate: true },
)

function handleUpdateImage(index: number, urls: string[]) {
  const url = urls[0] ?? ''
  const next = [...images.value]
  while (next.length <= index) {
    next.push('')
  }
  next[index] = url
  images.value = next
}

async function handleSave() {
  const validationResults = await Promise.all([
    formRef1.value?.validate(),
    formRef2.value?.validate(),
    formRef3.value?.validate(),
    formRef4.value?.validate(),
    formRef5.value?.validate(),
  ])

  const allValid = validationResults.every(result => result?.valid)

  if (!allValid) {
    return
  }

  const uploadPromises = images.value.map(url => url ? uploadFromBlobUrl(url) : Promise.resolve(null))
  const uploadedUrls = await Promise.all(uploadPromises)

  const finalImages = images.value.map((image, index) =>
    uploadedUrls[index] || image,
  )

  if (!validateTemplateImages({
    requiredImageCount: 5,
    images: finalImages,
  })) {
    return
  }
  await loading.wrapLoading(async () => {
    const templateContent = {
      id: templateId.five_template,
      title: resolvedProjectTitle.value,
      description: formModel.value.description,
      leftSectionTitle: formModel.value.leftSectionTitle,
      leftSectionDescription: formModel.value.leftSectionDescription,
      rightSectionTitle: formModel.value.rightSectionTitle,
      rightSectionDescription: formModel.value.rightSectionDescription,
      bottomSectionTitle: formModel.value.bottomSectionTitle,
      bottomSectionDescription: formModel.value.bottomSectionDescription,
      bottomSecondarySectionTitle: formModel.value.bottomSecondarySectionTitle,
      bottomSecondaryDescription: formModel.value.bottomSecondaryDescription,
      buttons: getButtonsForSave(),
      images: {
        triple1: finalImages[0],
        triple2: finalImages[1],
        triple3: finalImages[2],
        bottom1: finalImages[3],
        bottom2: finalImages[4],
      },
    }

    await saveTemplate(templateContent as IFiveTemplateData)
    clearDraft()

    await navigateAfterTemplateSave(
      router,
      route.query,
      isProjectRequestContext.value
        ? (route.params.id as string)
        : routeParamForLinks.value,
    )
  })
}
</script>

<style lang="scss" scoped>
.five-template {
  display: grid;
  gap: rem(60px);
  grid-template-columns: 1fr;

  &__header {
    display: grid;
    gap: rem(16px);
  }

  &__main-section {
    display: grid;
    grid-template-columns: 3fr 2fr;
    gap: rem(32px);

    @include bp-media('xs', 'sm') {
      grid-template-columns: 1fr;
      gap: rem(24px);
    }
  }

  &__two-section {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: rem(28px);

    @include bp-media('xs') {
      grid-template-columns: 1fr;
    }

    @include bp-media('sm','md') {
      gap: rem(24px);
    }
  }

  &__main-image {
    display: flex;
  }

  &__side-images {
    display: grid;
    gap: rem(20px);
  }

  &__dual-headers {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: rem(32px);

    @include bp-media('xs', 'sm') {
      grid-template-columns: 1fr;
      gap: rem(24px);
    }
  }

  &__header-left,
  &__header-right {
    display: grid;
    gap: rem(16px);
  }

  &__triple-images {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: rem(32px);

    @include bp-media('xs', 'sm', 'md') {
      gap: rem(16px);
    }

    @include bp-media('xs', 'sm') {
      grid-template-columns: 1fr;
      gap: rem(24px);
    }
  }

  &__additional-section {
    display: grid;
    gap: rem(16px);
  }

  &__section-marker {
    width: rem(56px);
    height: rem(56px);
  }
}
</style>
